"use client";
import { SegmentControl } from "@/components/SegmentControl";
import { Translate } from "@/components/Translate";
import { Close24 } from "@/components/icons/Close24";
import {
	AccordionContent,
	AccordionIndicator,
	AccordionItem,
	AccordionRoot,
	AccordionTrigger,
} from "@/components/ui/Accordion";
import { IconButton } from "@/components/ui/IconButton";
import { Prose } from "@/components/ui/Prose";
import { RichTextRender } from "@/components/ui/RichTextRender";
import type { GlobalData } from "@/features/global/dal";
import { useTranslations } from "@/i18n/client";
import type { ProductDetails } from "@/lib/centra";
import type { AssetStoryblokCompat } from "@/types/storyblok-compat";
import { getMedia } from "@frend-digital/storyblok";
import {
	DialogClose,
	DialogContent,
	DialogRoot,
	DialogTrigger,
	DialogViewport,
} from "@frend-digital/ui";

import { useEffect, useRef, useState } from "react";
import styles from "../ProductDetailsPage/ProductDetailsPage.module.css";
import { ReviewList } from "./Reviews/ReviewList";
import { StyleWithProducts } from "./StyleWithProducts";

export const ProductAccordions = ({
	product,
	slug,
	preview,
	shippingReturnsGuarantee,
}: {
	product: ProductDetails;
	slug: string[];
	preview: boolean;
	shippingReturnsGuarantee?: GlobalData["shippingReturnsGuarantee"];
}) => {
	const t = useTranslations();
	const guaranteeIcon = getMedia(shippingReturnsGuarantee?.icon as AssetStoryblokCompat);
	const guaranteeFullSizeIcon = getMedia(
		shippingReturnsGuarantee?.fullSizeIcon as AssetStoryblokCompat,
	);
	const [fullSizeIconOpen, setFullSizeIconOpen] = useState(false);
	const [showReviewsAccordion, setShowReviewsAccordion] = useState<boolean | undefined>(undefined);
	const currentProductSkuRef = useRef<string | undefined>(product.sku);

	useEffect(() => {
		if (currentProductSkuRef.current !== product.sku) {
			setShowReviewsAccordion(undefined);
			currentProductSkuRef.current = product.sku;
		}
		let observer: MutationObserver | null = null;

		const lipscoreDiv = document.getElementById("lipscore-review-list");

		const checkReviewCount = (element: HTMLElement) => {
			if (element.hasAttribute("data-ls-product-review-count")) {
				const reviewCount = Number.parseInt(element.dataset.lsProductReviewCount || "0", 10);
				setShowReviewsAccordion(reviewCount > 0);

				if (observer) {
					observer.disconnect();
				}
			}
		};

		if (lipscoreDiv) {
			checkReviewCount(lipscoreDiv);

			if (showReviewsAccordion === undefined) {
				observer = new MutationObserver((mutationsList) => {
					for (const mutation of mutationsList) {
						if (
							mutation.type === "attributes" &&
							mutation.attributeName === "data-ls-product-review-count"
						) {
							checkReviewCount(mutation.target as HTMLElement);
							break;
						}
					}
				});

				observer.observe(lipscoreDiv, { attributes: true });
			}
		}

		return () => {
			if (observer) {
				observer.disconnect();
			}
		};
	}, [product.sku, showReviewsAccordion]);

	return (
		<section>
			<AccordionRoot>
				{!!product.usp?.length && (
					<AccordionItem open>
						<AccordionTrigger>
							<Translate>Features</Translate>
							<AccordionIndicator />
						</AccordionTrigger>
						<AccordionContent>
							<ul className={styles.list}>
								{product.usp.map((usp) => (
									<li key={usp}>{usp}</li>
								))}
							</ul>
						</AccordionContent>
					</AccordionItem>
				)}

				{product.descriptionHTML && (
					<AccordionItem>
						<AccordionTrigger>
							<Translate>Description</Translate>
							<AccordionIndicator />
						</AccordionTrigger>

						<AccordionContent>
							{/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
							<Prose small dangerouslySetInnerHTML={{ __html: product.descriptionHTML }} />
							<br />
							<span className={styles.sku}>
								<small>SKU: {product.sku}</small>
							</span>
						</AccordionContent>
					</AccordionItem>
				)}

				{!!product.materials?.length && (
					<AccordionItem>
						<AccordionTrigger>
							<Translate>Materials</Translate>
							<AccordionIndicator />
						</AccordionTrigger>
						<AccordionContent>
							<ul className={styles.list}>
								{product.materials.map((material) => (
									<li key={material.value + material.type}>
										<b>
											<Translate>{material.type}</Translate>
										</b>
										: <Translate>{material.value}</Translate>
									</li>
								))}
							</ul>
						</AccordionContent>
					</AccordionItem>
				)}

				{(shippingReturnsGuarantee?.text || guaranteeIcon) && (
					<SegmentControl segments={{ countries: shippingReturnsGuarantee?.countries }}>
						<AccordionItem>
							<AccordionTrigger>
								{shippingReturnsGuarantee?.title || (
									<Translate>Shipping, Returns & Guarantee</Translate>
								)}
								<AccordionIndicator />
							</AccordionTrigger>
							<AccordionContent>
								{shippingReturnsGuarantee?.text && (
									<RichTextRender blok={shippingReturnsGuarantee.text} preview={preview} small />
								)}
								{guaranteeIcon && guaranteeFullSizeIcon && (
									<DialogRoot open={fullSizeIconOpen} onOpenChange={setFullSizeIconOpen}>
										<DialogTrigger
											className={styles.guaranteeIconTrigger}
											aria-label={t("View full-size label")}
										>
											<img
												src={guaranteeIcon.url}
												alt={guaranteeIcon.alt || t("EU legal guarantee label")}
												loading="lazy"
												className={styles.guaranteeIcon}
											/>
										</DialogTrigger>
										<DialogViewport className={styles.guaranteeIconViewport}>
											<DialogContent className={styles.guaranteeIconContent}>
												<DialogClose asChild className={styles.guaranteeIconClose}>
													<IconButton aria-label={t("Close full-size label")}>
														<Close24 />
													</IconButton>
												</DialogClose>
												<img
													src={guaranteeFullSizeIcon.url}
													alt={guaranteeFullSizeIcon.alt || t("EU legal guarantee label")}
													className={styles.guaranteeIconFullImage}
												/>
											</DialogContent>
										</DialogViewport>
									</DialogRoot>
								)}
								{guaranteeIcon && !guaranteeFullSizeIcon && (
									<img
										src={guaranteeIcon.url}
										alt={guaranteeIcon.alt || t("EU legal guarantee label")}
										loading="lazy"
										className={styles.guaranteeIcon}
									/>
								)}
							</AccordionContent>
						</AccordionItem>
					</SegmentControl>
				)}
				{/* Hide "Style with" section if the product is for adults (men/women) */}
				{!product.categories?.some(
					(cat) => Number(cat.category) === 7 || Number(cat.category) === 15,
				) && <StyleWithProducts productIds={[product.items?.[0]?.item?.split("-")[1]]} />}

				{showReviewsAccordion !== false && (
					<AccordionItem>
						<AccordionTrigger>
							<Translate>Reviews</Translate>
							<AccordionIndicator />
						</AccordionTrigger>
						<AccordionContent>
							<ReviewList product={product} slug={slug} />
						</AccordionContent>
					</AccordionItem>
				)}
			</AccordionRoot>
		</section>
	);
};
